<script>
import toast from '@services/toasts/index.js'
import { logger } from '@/services/logging/AppLogger'
import baseMixin from '@/mixins/baseMixin'
import BookingListTabKey from '@/shared/constants/booking/BookingListTabKey'
import BookingStatusLabelType from '@/shared/constants/booking/BookingStatusLabelType'
import ExportMode from '@/shared/constants/booking/ExportMode'
import ExportDateRangeType from '@/shared/constants/booking/ExportDateRange'
import ExportFilterType from '@/shared/constants/booking/ExportFilterType'
import { Conditions } from '@/shared/constants/datePicker/picker'
import IncludeCheckboxes from '@/components/booking/bookingList/export/include-checkboxes.vue'
import ExportWhat from '@/components/booking/bookingList/export/export-what.vue'
import ExportColumns from './export/export-columns.vue'
import ExportDateRange from './export/export-date-range.vue'

export default {
  name: 'ExportBookings',
  components: {
    ExportDateRange,
    IncludeCheckboxes,
    ExportWhat,
    ExportColumns,
  },
  mixins: [baseMixin],
  props: {
    exportMode: { type: String, default: ExportMode.All },
    selectedCount: { type: Number, default: 0 },
    selectedBookings: { type: Array, default: () => [] },
    filters: { type: Object, required: true, default: () => ({}) },
  },
  emits: ['unselected-rows'],
  data() {
    return {
      isExporting: false,
      customDateRange: [this.filters?.startDate, this.filters?.endDate],
      selectedRange: ExportDateRangeType.Custom,
      showDropdown: false,
      condition: this.filters?.dateFilterCondition ?? Conditions.IS,
      dropdownTriggerEl: null,
      isDateRangePicker: false,
      dropdownCoords: { top: 0, left: 0 },
      include: {
        active: [
          BookingListTabKey.all,
          BookingListTabKey.upcoming,
          BookingListTabKey.past,
        ].includes(this.filters?.tabType),
        cancelled: this.filters?.tabType === BookingListTabKey.cancelled,
        unapproved:
          this.filters?.tabType === BookingListTabKey.approvalRequired,
      },
      exportType: ExportFilterType.Filtered,
      selectedColumns: {},
    }
  },
  computed: {
    description() {
      if (!this.isSelectedMode) {
        return this.$t('exportBookings.description.default')
      } else {
        return this.$tc(
          'exportBookings.description.selected',
          this.selectedCount,
          {
            count: this.selectedCount,
            bookingType: this.isBlockBookingMode
              ? this.$t('exportBookings.description.blockBookingType')
              : '',
          }
        )
      }
    },
    isBlockBookingMode() {
      return this.filters?.isBlockBooking ?? false
    },
    isSelectedMode() {
      return this.exportMode === ExportMode.Selected
    },
  },
  methods: {
    /**
     * Formats a Date object to ISO date string (YYYY-MM-DD).
     * @param {Date} date - Date to format.
     * @returns {string} Formatted date string.
     */
    formatISO(date) {
      const pad = (number) => String(number).padStart(2, '0')
      const yyyy = date.getFullYear()
      const mm = pad(date.getMonth() + 1)
      const dd = pad(date.getDate())
      return `${yyyy}-${mm}-${dd}`
    },

    /**
     * Returns a Date offset by the specified number of days from today.
     * @param {number} days - Number of days to offset (negative for past).
     * @returns {Date} Offset date at midnight.
     */
    offsetDays(days) {
      const date = new Date()
      date.setHours(0, 0, 0, 0)
      date.setDate(date.getDate() + days)
      return date
    },

    /**
     * Computes start/end dates for custom date range.
     * @returns {{ startDate: string|null, endDate: string|null }}
     */
    computeDateRangeForCustom() {
      let startDate = null
      let endDate = null

      const [customRangeStartDate, customRangeEndDate] = this.customDateRange

      switch (this.condition) {
        case Conditions.IS:
          if (customRangeStartDate) {
            const date = this.formatISO(new Date(customRangeStartDate))
            startDate = date
            endDate = date
          }
          break
        case Conditions.BEFORE:
          if (customRangeStartDate) {
            endDate = this.formatISO(new Date(customRangeStartDate))
            startDate = ''
          }
          break
        case Conditions.AFTER:
          if (customRangeStartDate) {
            startDate = this.formatISO(new Date(customRangeStartDate))
            endDate = ''
          }
          break
        case Conditions.BETWEEN:
          if (customRangeStartDate)
            startDate = this.formatISO(new Date(customRangeStartDate))
          if (customRangeEndDate)
            endDate = this.formatISO(new Date(customRangeEndDate))
          break
        default:
          throw new Error(`Invalid condition: ${this.condition}`)
      }

      return { startDate, endDate }
    },

    /**
     * Computes start/end dates based on selected range and condition.
     * @returns {{ startDate: string|null, endDate: string|null }}
     */
    computeDateRangeByRangeType() {
      let startDate = null
      let endDate = null

      switch (this.selectedRange) {
        case ExportDateRangeType.Last7Days:
          endDate = this.formatISO(this.offsetDays(0))
          startDate = this.formatISO(this.offsetDays(-6))
          return { startDate, endDate }
        case ExportDateRangeType.Last30Days:
          endDate = this.formatISO(this.offsetDays(0))
          startDate = this.formatISO(this.offsetDays(-29))
          return { startDate, endDate }
        case ExportDateRangeType.Custom:
          return this.computeDateRangeForCustom()
        default:
          throw new Error(`Invalid range type: ${this.selectedRange}`)
      }
    },

    /**
     * Calculates min/max date range from selected bookings, including block details.
     * @returns {{ startDate: string|null, endDate: string|null }}
     */
    getSelectedBookingsDateRange() {
      const selectedBookings = this.selectedBookings || []
      const allStartDates = []
      const allEndDates = []

      selectedBookings.forEach((booking) => {
        if (booking?.startDateTimeLocal) {
          allStartDates.push(new Date(booking.startDateTimeLocal))
        }
        if (booking?.endDateTimeLocal) {
          allEndDates.push(new Date(booking.endDateTimeLocal))
        }

        if (this.isBlockBookingMode && Array.isArray(booking?.blockDetails)) {
          booking.blockDetails.forEach((detail) => {
            if (detail?.startDateTimeLocal) {
              allStartDates.push(new Date(detail.startDateTimeLocal))
            }
            if (detail?.endDateTimeLocal) {
              allEndDates.push(new Date(detail.endDateTimeLocal))
            }
          })
        }
      })

      if (allStartDates.length && allEndDates.length) {
        return {
          startDate: this.formatYMD(new Date(Math.min(...allStartDates))),
          endDate: this.formatYMD(new Date(Math.max(...allEndDates))),
        }
      }

      return { startDate: null, endDate: null }
    },

    /**
     * Resolves booking statuses based on include checkboxes and filters.
     * @param {boolean} [useFilters=false] - Whether to include existing filter statuses.
     * @returns {string[]} Array of status strings.
     */
    resolveExportStatus(useFilters = false) {
      const { include, filters } = this

      const BOOKING_STATUS = {
        open: BookingStatusLabelType.Open.toLowerCase(),
        confirmed: BookingStatusLabelType.Confirmed.toLowerCase(),
        pending: BookingStatusLabelType.Pending.toLowerCase(),
        cancelled: BookingStatusLabelType.Cancelled.toLowerCase(),
        unapproved: BookingStatusLabelType.Unapproved.toLowerCase(),
      }

      if (include.cancelled && !include.active && !include.unapproved) {
        return [BOOKING_STATUS.cancelled]
      }

      if (include.unapproved && !include.active && !include.cancelled) {
        return filters?.tabType === BookingListTabKey.approvalRequired
          ? [BOOKING_STATUS.unapproved]
          : [BOOKING_STATUS.open]
      }

      let status =
        useFilters && filters.status?.length ? [...filters.status] : []

      if (include.active) {
        if (!useFilters || !filters.status?.length) {
          status.push(
            BOOKING_STATUS.open,
            BOOKING_STATUS.confirmed,
            BOOKING_STATUS.pending
          )
        }
        status = status.filter((s) => s !== BOOKING_STATUS.cancelled)
      }

      if (include.cancelled && !status.includes(BOOKING_STATUS.cancelled)) {
        status.push(BOOKING_STATUS.cancelled)
      }

      if (include.unapproved && !status.includes(BOOKING_STATUS.open)) {
        status.push(BOOKING_STATUS.open)
      }

      return [...new Set(status)]
    },

    /**
     * Extracts non-empty filter arrays from current filters.
     * @returns {Object} Filter fields with values.
     */
    getFilterFields() {
      const { filters } = this
      const filterKeys = [
        'clientids',
        'educators',
        'classifications',
        'certifications',
        'grades',
        'replacings',
        'locations',
      ]

      return filterKeys.reduce((acc, key) => {
        if (filters[key]?.length) {
          acc[key] = filters[key]
        }
        return acc
      }, {})
    },

    /**
     * Constructs the export API payload.
     * @param {Object} params
     * @param {string|null} params.startDate
     * @param {string|null} params.endDate
     * @param {number[]|null} params.bookingIds
     * @param {string[]} params.selectedColumns
     * @returns {Object} API payload.
     */
    buildExportPayload({ startDate, endDate, bookingIds, selectedColumns }) {
      let payload = {
        columns: selectedColumns,
        startDate,
        endDate,
        bookingIds,
        isBlockBooking: this.isBlockBookingMode,
        tabType: this.filters?.tabType ?? 0,
        isDescending: this.filters?.isDescending ?? false,
        ...(this.filters.sortColumn && { sortColumn: this.filters.sortColumn }),
      }

      const isFiltered = this.exportType !== ExportFilterType.All
      payload.status = this.resolveExportStatus(isFiltered)

      if (isFiltered) {
        payload = { ...payload, ...this.getFilterFields() }
      }

      if (this.isSelectedMode && this.selectedCount) {
        const { status, ...rest } = payload
        return rest
      }

      return payload
    },

    /**
     * Dispatches export action with timeout protection.
     * @param {Object} payload - Export request payload.
     * @param {number} [timeoutMs=60000] - Timeout in milliseconds.
     * @returns {Promise<any>} API response.
     */
    async executeExportWithTimeout(payload, timeoutMs = 60000) {
      let timer
      const timeout = new Promise((resolve, reject) => {
        timer = setTimeout(() => {
          reject(new Error(`Export request timed out after ${timeoutMs}ms`))
        }, timeoutMs)
      })

      try {
        const res = await Promise.race([
          this.$store.dispatch('bookings/getExportData', payload),
          timeout,
        ])
        clearTimeout(timer)
        return res
      } catch (e) {
        clearTimeout(timer)
        throw e
      }
    },

    /**
     * Emits close events and resets export state.
     */
    cleanupExport() {
      this.$emit('close')
      this.$emit('unselected-rows')
      this.isExporting = false
    },

    /**
     * Initiates booking export: builds payload, calls API, downloads result.
     */
    async getExportData() {
      this.isExporting = true

      try {
        const selectedColumns = Object.entries(this.selectedColumns)
          .filter(([_, value]) => value)
          .map(([key]) => key)

        let startDate, endDate, bookingIds, range

        switch (this.exportMode) {
          case ExportMode.Selected:
            range = this.getSelectedBookingsDateRange()
            startDate = range.startDate ?? this.filters?.startDate
            endDate = range.endDate ?? this.filters?.endDate
            bookingIds = this.selectedBookings.map((b) => b.id)
            break
          case ExportMode.All:
            range = this.computeDateRangeByRangeType()
            startDate = range.startDate
            endDate = range.endDate
            break
          default:
            throw new Error(`Invalid export mode: ${this.exportMode}`)
        }

        const payload = this.buildExportPayload({
          startDate,
          endDate,
          bookingIds,
          selectedColumns,
        })

        const res = await this.executeExportWithTimeout(payload)

        const blob = await this.normalizeToBlob(res)
        if (!blob || !blob.size) {
          logger.logError(new Error('Empty export blob'))
          return
        }

        this.downloadBlob(blob, this.suggestFileName())
        await this.$nextTick()
        this.cleanupExport()
      } catch (e) {
        logger.logError(e)
        toast.error(e.message || 'Export failed')
        this.cleanupExport()
      }
    },

    /**
     * Converts various response types to a Blob.
     * @param {any} result - API response to normalize.
     * @returns {Promise<Blob>} CSV blob.
     */
    async normalizeToBlob(result) {
      if (
        result &&
        typeof result === 'object' &&
        typeof result.blob === 'function' &&
        'body' in result
      ) {
        return await result.blob()
      }

      if (result instanceof Blob) return result
      if (result && result.data instanceof Blob) return result.data

      if (result instanceof ArrayBuffer)
        return new Blob([result], { type: 'text/csv;charset=utf-8' })
      if (result && result.data instanceof ArrayBuffer) {
        return new Blob([result.data], { type: 'text/csv;charset=utf-8' })
      }

      if (typeof result === 'string') return this.stringToCsvBlob(result)
      if (result && typeof result.data === 'string')
        return this.stringToCsvBlob(result.data)
      if (result && typeof result.payload === 'string')
        return this.stringToCsvBlob(result.payload)

      if (this.isReadableStream(result)) {
        const text = await this.readStreamToText(result)
        return this.stringToCsvBlob(text)
      }

      console.warn('Unknown export result shape:', {
        type: typeof result,
        ctor: result?.constructor?.name,
        keys: result ? Object.keys(result) : null,
      })
      return new Blob([], { type: 'text/csv;charset=utf-8' })
    },

    /**
     * Checks if an object is a ReadableStream.
     * @param {any} obj - Object to check.
     * @returns {boolean}
     */
    isReadableStream(obj) {
      return obj && typeof obj.getReader === 'function'
    },

    /**
     * Reads a ReadableStream to text.
     * @param {ReadableStream} stream - Stream to read.
     * @returns {Promise<string>} Stream contents as text.
     */
    async readStreamToText(stream) {
      const reader = stream.getReader()
      const decoder = new TextDecoder('utf-8')
      let chunks = ''
      while (true) {
        const { value, done } = await reader.read()
        if (done) break
        chunks += decoder.decode(value, { stream: true })
      }
      chunks += decoder.decode()
      return chunks
    },

    /**
     * Generates a filename for the export based on mode and date.
     * @returns {string} Suggested filename.
     */
    suggestFileName() {
      const pad = (n) => String(n).padStart(2, '0')
      const d = new Date()
      const yyyy = d.getFullYear()
      const mm = pad(d.getMonth() + 1)
      const dd = pad(d.getDate())
      const stamp = `${yyyy}-${mm}-${dd}`

      return this.isSelectedMode
        ? `bookings_selected_${stamp}.csv`
        : `bookings_${this.selectedRange}_${stamp}.csv`
    },

    /**
     * Converts a string to a CSV blob with BOM for Excel compatibility.
     * @param {string} csvText - CSV content.
     * @returns {Blob} CSV blob.
     */
    stringToCsvBlob(csvText) {
      const BOM = '\uFEFF'
      return new Blob([BOM + csvText], { type: 'text/csv;charset=utf-8' })
    },

    /**
     * Triggers a browser download for a blob.
     * @param {Blob} blob - File blob to download.
     * @param {string} filename - Name for the downloaded file.
     */
    downloadBlob(blob, filename) {
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.setAttribute('download', filename)
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },

    /**
     * Formats a Date object to YYYY-MM-DD string.
     * @param {Date} d - Date to format.
     * @returns {string} Formatted date string.
     */
    formatYMD(d) {
      const pad = (n) => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    },
  },
}
</script>

<template>
  <div
    class="export-container"
    style="align-items: center; justify-content: center"
  >
    <div class="export-bookings">
      <div class="content">
        <h4 class="title">{{ $t('exportBookings.title') }}</h4>
        <p class="description"
          >{{ description }} &nbsp;<span>Excel file</span></p
        >

        <!-- Date Range -->
        <template v-if="!isSelectedMode">
          <label class="section-label">{{
            $t('exportBookings.dateRange.label')
          }}</label>
          <ExportDateRange
            :selected-range="selectedRange"
            :custom-date-range="customDateRange"
            :condition="condition"
            @update:selectedRange="selectedRange = $event"
            @update:customDateRange="customDateRange = $event"
            @update:condition="condition = $event"
          />
        </template>

        <!-- Include checkboxes -->
        <IncludeCheckboxes
          v-if="!isSelectedMode"
          :include="include"
          @update:include="include = $event"
        />

        <!-- Export What -->
        <ExportWhat v-model="exportType" :is-selected-mode="isSelectedMode" />

        <!-- Columns -->
        <ExportColumns @selected-columns="selectedColumns = $event" />
      </div>

      <!-- Buttons -->
      <div class="button-group">
        <BaseButton
          text
          outlined
          light
          width="83"
          height="44"
          :border-radius="'8px'"
          class="cancel-btn"
          @click="$emit('close')"
        >
          {{ $t('exportBookings.buttons.cancel') }}
        </BaseButton>
        <BaseButton
          width="77"
          height="44"
          :border-radius="'8px'"
          color="primary"
          class="export-btn"
          :loading="isExporting"
          :disabled="isExporting"
          @click="getExportData"
        >
          {{ $t('exportBookings.buttons.export') }}
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@import '@/styles/_colors.scss';
@import '@/styles/bookingList/booking-export.scss';
</style>
